import { Link, useParams } from 'react-router-dom';
import { useAcciones, useConfig, useDesactivarTabla, useEstado, usePedirRecarga, useVersiones } from '../api/hooks';
import { ConMotivo } from '../components/ConMotivo';
import { EstadoBadge } from '../components/EstadoBadge';
import { SiPuede } from '../components/SiPuede';
import { situacion } from '../utils/estadoTabla';
import { autor, duracion, fecha, haceCuanto, numero, ventana } from '../utils/formato';

const ACCION: Record<string, string> = { semilla: 'Semilla', crear: 'Alta', editar: 'Cambio', desactivar: 'Desactivación' };

export function TablaPage() {
  const { tabla = '' } = useParams();
  const config = useConfig();
  const estado = useEstado();
  const historial = useVersiones(tabla);
  const acciones = useAcciones(tabla);
  const desactivar = useDesactivarTabla(tabla);
  const recargar = usePedirRecarga(tabla);

  if (config.isLoading) return <p className="cargando">Cargando…</p>;
  if (config.error) return <div className="aviso aviso-error">{config.error.message}</div>;
  const t = config.data!.tablas.find((x) => x.tabla === tabla);
  if (!t) {
    return (
      <div className="aviso aviso-error">
        La tabla {tabla} no está en la configuración. <Link to="/tablas">Volver a la lista</Link>
      </div>
    );
  }
  const e = estado.data?.tablas.find((x) => x.tabla === tabla);
  const s = situacion(t, e);

  return (
    <>
      <p className="miga">
        <Link to="/tablas">Tablas</Link> / {t.tabla}
      </p>
      <div className="titulo">
        <h1>
          {t.tabla} <EstadoBadge situacion={s} />
        </h1>
        <div className="acciones">
          <SiPuede rol="dwh.editor">
            <Link className="boton boton-primario" to={`/tablas/${t.tabla}/editar`}>
              Editar
            </Link>
            {t.activa && (
              <ConMotivo
                texto="Desactivar"
                confirmar="Desactivar la tabla"
                ayuda="Motivo del cambio (la réplica no se borra, solo deja de sincronizarse):"
                peligro
                alConfirmar={(motivo) => desactivar.mutateAsync({ motivo_cambio: motivo, version_base: config.data!.id })}
              />
            )}
          </SiPuede>
          <SiPuede rol="dwh.admin">
            <ConMotivo
              texto="Recargar entera"
              confirmar="Pedir la recarga"
              ayuda="Motivo (Airflow la recargará entera desde X3 en su próxima pasada):"
              alConfirmar={(motivo) => recargar.mutateAsync(motivo)}
            />
          </SiPuede>
        </div>
      </div>
      <p className="subtitulo">{t.motivo}</p>

      <div className="rejilla">
        <section className="tarjeta">
          <h2>Configuración</h2>
          <dl>
            <dt>Activa</dt>
            <dd>{t.activa ? 'Sí' : 'No'}</dd>
            <dt>Altas cada</dt>
            <dd>{duracion(t.frecuencia)}</dd>
            <dt>Cambios cada</dt>
            <dd>{duracion(t.cambios)}</dd>
            <dt>Bajas cada</dt>
            <dd>{duracion(t.bajas)}</dd>
            <dt>Margen de cambios</dt>
            <dd>{duracion(t.margen)}</dd>
            <dt>Ventana</dt>
            <dd>{ventana(t.ventana_ini, t.ventana_fin)}</dd>
            <dt>Filtro</dt>
            <dd>{t.filtro ? <code>{t.filtro}</code> : 'sin filtro'}</dd>
            <dt>Carga inicial</dt>
            <dd>{t.carga_unica ? 'en una sola lectura' : 'por lotes'}</dd>
            <dt>LOB</dt>
            <dd>{t.excluir_lob ? 'excluidos' : 'incluidos'}</dd>
          </dl>
          <h3>Columnas</h3>
          {t.columnas === '*' ? (
            <p>Todas (ROWID y UPDDATTIM_0 van siempre).</p>
          ) : (
            <ul className="columnas">
              {t.columnas.split(',').map((c) => (
                <li key={c}>
                  <code>{c}</code>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="tarjeta">
          <h2>Estado</h2>
          {!e ? (
            <p className="vacio">Airflow todavía no ha enviado el estado de esta tabla.</p>
          ) : (
            <dl>
              <dt>Últimas altas</dt>
              <dd>{haceCuanto(e.ult_altas)}</dd>
              <dt>Últimos cambios</dt>
              <dd>{haceCuanto(e.ult_cambios)}</dd>
              <dt>Últimas bajas</dt>
              <dd>{haceCuanto(e.ult_bajas)}</dd>
              <dt>Filas</dt>
              <dd>{numero(e.filas)}</dd>
              <dt>Carga inicial</dt>
              <dd>{e.carga_en_curso ? `en curso desde ${fecha(e.carga_inicio)}` : e.carga_fin ? `terminada ${fecha(e.carga_fin)}` : '—'}</dd>
              <dt>Cursor de altas</dt>
              <dd>{numero(e.max_rowid)}</dd>
              {e.ult_error && (
                <>
                  <dt>Último error</dt>
                  <dd className="error-texto">
                    {fecha(e.ult_error_en)}: {e.ult_error}
                  </dd>
                </>
              )}
            </dl>
          )}
        </section>
      </div>

      <section>
        <h2>Historial de cambios</h2>
        <table className="tabla">
          <thead>
            <tr>
              <th>Versión</th>
              <th>Fecha</th>
              <th>Qué</th>
              <th>Quién</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {(historial.data ?? []).map((h) => (
              <tr key={h.id}>
                <td>
                  <Link to={`/versiones/${h.id}`}>{h.id}</Link>
                </td>
                <td>{fecha(h.created_at)}</td>
                <td>{ACCION[h.accion] ?? h.accion}</td>
                <td>{autor(h.autor)}</td>
                <td>{h.motivo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {(acciones.data ?? []).length > 0 && (
        <section>
          <h2>Acciones manuales</h2>
          <table className="tabla">
            <thead>
              <tr>
                <th>Pedida</th>
                <th>Qué</th>
                <th>Quién</th>
                <th>Motivo</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {acciones.data!.map((a) => (
                <tr key={a.id}>
                  <td>{fecha(a.created_at)}</td>
                  <td>{a.tipo}</td>
                  <td>{autor(a.solicitante)}</td>
                  <td>{a.motivo}</td>
                  <td title={a.resultado ?? undefined}>
                    {a.estado}
                    {a.resuelta_at ? ` (${fecha(a.resuelta_at)})` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}
